import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers/root";
import { PropertyListState, UserProperty } from "../../../redux/reducers/getPropertySelectionListReducer/propertySelectionListReducer";
import AmpPagination from "../../common/AmpPagination/AmpPagination";
import styles from "./Edit.module.css";
import AmpCheckBox from "../../common/AmpCheckBox/AmpCheckBox";
import SmallHeading from "../../common/AmpSmallHeading/AppSmallHeading";
import { useTranslation } from "react-i18next";
import AmpButton from "../../common/AmpButton/AmpButton";
import AttestText from "../../Registration/AttestationEnotifcation/AttestText";
import AmpInput from "../../common/AmpInput/AmpInput";
import { EnotifPayload, EnotifPendingChange, updateEnotifs } from "../../../redux/actions/enotif/enotif";
import AmpLoadingSpinner from "../../common/AmpLoadingSpinner/AmpLoadingSpinner";
import { BaseAsyncReducerState } from "../../../redux/types";
import { getPropertySelectionList } from "../../../redux/actions/propertySelectionList/propertySelectionList";
import { ENOTIFICATION_SUBMIT, GET_PROPERTY_LIST } from "../../../redux/actionTypes/actionsTypes";
import { setLargeToast } from "../../../redux/actions/toastNotification/toastNotification";
import { changeModalContent, changeModalState } from "../../../redux/actions/modal/modal";
import { filterPropertyList } from "../../../utils/filterPropertyList";
import EnoticiationForm, { EnotifFormData } from "../EnotificationForm/EnotificationForm";
import EditRow from "./EditRow";
import AmpFormError from "../../common/AmpFormError/AmpFormError";

export interface EditEnotifcationProps {
    isPreview?: boolean;
}

export default function EditEnotifcation(props: EditEnotifcationProps) {

    const { t } = useTranslation(["login", "propertySelectionList", "property"]);
    const dispatch = useDispatch();

    const propertySelectionList = useSelector((state: RootState) => state.propertyListReducer.propertySelectionList as PropertyListState);
    const enotifState: BaseAsyncReducerState = useSelector((state: RootState) => state.enotifReducers.enotif);
    const [currentPage, setCurrentPage] = useState(1);
    // eslint-disable-next-line
    const [pagesize, setPageSize] = useState(10);
    const [keyOffset, setKeyOffset] = useState(30);

    const [pendingChanges, setPendingChanges] = useState<EnotifPendingChange[]>([]);
    const [searchFilter, setFilter] = useState("");
    const disabledPending = pendingChanges.filter((change) => !change.enotif_enabled);
    const enabledPending = pendingChanges.filter((change) => change.enotif_enabled);

    const [pendingCurrPage, setPendingCurrentPage] = useState(1);
    const [currentStep, setCurrentStep] = useState(1);

    const [disabledConsented, setDisabledConsented] = useState(false);
    const [enabledConsented, setEnabledConsented] = useState(false);

    const [attestValid, setAttestValid] = useState(false);
    const email = localStorage.getItem("email");
    const userProfileId = localStorage.getItem("userProfileId");

    const [enotifPayload, setEnotifPayload] = useState<EnotifPayload | false>(false);
    const filteredList = propertySelectionList.properties && filterPropertyList([...propertySelectionList.properties], searchFilter)

    const showActionsBar = propertySelectionList.properties && propertySelectionList.properties.length > 10;
    const [bulkChangeFlag, setBulkChangeFlag] = useState(false);

    const [checkFormKey, updateCheckFormKey] = useState(0);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (enotifState.success && !enotifState.loading) {
            cancel();
            dispatch({ type: GET_PROPERTY_LIST.RESET });
            dispatch(getPropertySelectionList(userProfileId as string));
            setAttestValid(false);
            setCurrentStep(1);
            setPendingChanges([]);
            dispatch(changeModalState("modal-fade-out"));
            dispatch({ type: ENOTIFICATION_SUBMIT.RESET })
            dispatch(changeModalContent(<></>));
            // TODO enotification to electronic notification
            dispatch(setLargeToast(t("enotif-confirm"), "success", true));
        } else if([403, 400, 500].includes(enotifState.status as number) && !enotifState.loading) {
            setHasError(true);
        }
    }, [enotifState, dispatch, userProfileId, t])

    const handleEnotifChange = (prop: UserProperty, isChecked: boolean,) => {
        const pending = [...pendingChanges];
        const pendingObject: EnotifPendingChange | undefined = pending.find((p) => p.roll === prop.roll_num, pending);
        if (pendingObject) {
            // if this change differs from what is already pending do nothing
            if (pendingObject.enotif_enabled !== isChecked) {
                setPendingChanges(pending.filter(p => p.roll !== prop.roll_num));
            }
            // New pending object
        } else {
            const newPending: EnotifPendingChange = {
                roll: prop.roll_num,
                enotif_enabled: isChecked,
                address: prop.address
            }
            pending.push(newPending);
            setPendingChanges(pending)
        }
    }

    const buildList = () => {
        if (propertySelectionList.properties) {
            // Theres probably a better way to combine the state changes
            const rows = filteredList?.map((property: UserProperty, index: number) => {
                const pagesizeProps = props.isPreview ? 5 : pagesize;
                const enotifAria = `${index}${property.roll_num}enotif${String(props.isPreview && "prev")}${userProfileId}`;
                const pendingValue = pendingChanges.find(p => p.roll === property.roll_num);
                const enotifNotAllowed = property.enotif_allowed === "False";
                const enotifValue = pendingValue ? pendingValue?.enotif_enabled : property.enotif_choice === "True";
                const rowKey = `${enotifAria} ${(index + keyOffset)}`
                const show = index < Math.ceil(currentPage * pagesizeProps) && index >= (currentPage - 1) * pagesizeProps;
                if (show) {
                    return (
                        <EditRow
                            key={rowKey}
                            index={rowKey}
                            pendingValue={pendingValue}
                            enotifNotAllowed={enotifNotAllowed}
                            enotifValue={enotifValue}
                            handleEnotifChange={handleEnotifChange}
                            property={property}
                            enotifAria={enotifAria}
                            isPreview={props.isPreview} />
                    )
                } else {
                    return null;
                }

            })
            return rows
        }
        return [];
    }

    const buildPendingList = (flag: boolean) => {
        if (pendingChanges.length) {
            // Theres probably a better way to combine the state changes
            const filteredRows = pendingChanges.filter((change) => change.enotif_enabled === flag);
            const rows = filteredRows.map((pendingChange: EnotifPendingChange, index: number) => {

                const enotifAria = `${index}${pendingChange.roll}enotif`;
                const show = index < Math.ceil(pendingCurrPage * 5) && index >= (pendingCurrPage - 1) * 5;
                if (show) {
                    return (
                        <div className={`${styles.EditRow} ${styles.Pending}`} key={index}>
                            <span>
                                <AmpCheckBox disabled={true} defaultChecked={pendingChange.enotif_enabled || false} isSlider={true} handleCheck={(e) => { return null; }} id={enotifAria} label={""} hasToolTip={undefined} toolTip={""} />
                            </span>
                            <div>{pendingChange.roll}</div>
                            <div>{pendingChange.address}</div>
                        </div>
                    );
                } else {
                    return null;
                }
            })
            return rows
        }
        return [];
    }

    const toggleNextStep = (nextStep: number, isBack: boolean) => {
        if (nextStep === 2) {
            if (disabledPending.length < 1) {
                isBack ? setCurrentStep(1) : setCurrentStep(3);
                setDisabledConsented(true);
                return;
            }
        } else if (nextStep === 3) {
            if (enabledPending.length < 1) {
                isBack ? setCurrentStep(2) : setCurrentStep(4);
                setEnabledConsented(true);
                return;
            }
        }
        setCurrentStep(nextStep)
        setPendingCurrentPage(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const formCallback = (formData: EnotifFormData) => {
        if (formData.firstname && formData.lastname && formData.phone && email) {
            const payload: EnotifPayload = {
                entries: [...pendingChanges],
                ...formData,
                user_profile_id: userProfileId as string
            }
            setAttestValid(true);
            setEnotifPayload(payload);
            dispatch(updateEnotifs(payload));
            console.log(payload);
        }
    }

    const submit = () => {
        console.log(enotifPayload);
        setHasError(false);
        updateCheckFormKey(checkFormKey + 1);
    }

    const cancel = () => {
        setHasError(false);
        setCurrentStep(1);
        setPendingCurrentPage(1);
        setCurrentPage(1);
        setEnotifPayload(false);
        setPendingChanges([]);
        setDisabledConsented(false);
        setEnabledConsented(false);
    }

    const handleBulkChange = (checked: boolean) => {
        if (propertySelectionList.properties) {
            const changes: EnotifPendingChange[] = (propertySelectionList.properties.map((prop, index) => {
                if (prop.enotif_allowed === "True" && (prop.enotif_choice === "True") !== checked) {
                    return {
                        roll: prop.roll_num,
                        enotif_enabled: checked,
                        address: prop.address
                    }
                } else {
                    return false
                }
            }).filter((elm) => elm !== false))
            setPendingChanges(changes);
            setCurrentPage(1);
            setKeyOffset(keyOffset + 50);
            setBulkChangeFlag(!bulkChangeFlag)
        }
    }

    const onSearch = (search: string) => {
        setFilter(search);
    }

    const resetBulk = () => {
        setPendingChanges([]);
        setKeyOffset(keyOffset + 70);
    }

    // const stepTitles = [
    //     "Edit",
    //     "Confirm Paper",
    //     "Confirm Electronic",
    //     "Submit"
    // ]

    return (<>
        {props.isPreview && (<div>
            <div className={styles.EditRow} key={0}>
                <SmallHeading allCaps for={""}>{t("4-enotif-heading")}</SmallHeading>
                <SmallHeading allCaps for={""}>{t("property-list-table.header.column-1", { ns: "propertySelectionList" })}</SmallHeading>
                <SmallHeading allCaps for={""}>{t("property-list-table.header.column-2", { ns: "propertySelectionList" })}</SmallHeading>
            </div>
            {buildList()}
        </div>)}
        {!props.isPreview && (
            <>
                <div className={styles.Status}>
                    {pendingChanges.length > 0 && (<>
                        <div className={styles.PendingNumber}>{pendingChanges.length}</div>
                        <div className={styles.Unsaved}>{currentStep === 1 ? t("unsaved") : t("unsaved-change")}</div>
                    </>)}
                </div>
                <div>
                    {/* <AmpStepper currentStep={currentStep} numSteps={0} stepTitles={stepTitles} /> */}
                    {currentStep === 1 && (<>
                        <div className={styles.EditTop}>
                            {showActionsBar && (
                                <div className={styles.ActionRow}>
                                    <AmpCheckBox
                                        label={!bulkChangeFlag ? t("select-all"): t("deselect-all")}
                                        isSlider={true}
                                        id={"bulktoggle"}
                                        hasToolTip={undefined}
                                        toolTip={""}
                                        handleCheck={(e: React.ChangeEvent<HTMLInputElement>) => { handleBulkChange(e.currentTarget?.checked) }} />
                                    <AmpButton onClick={() => resetBulk()} type={"submit"} text={t("6-reset")} atlStyle />
                                    <AmpInput name="searchBox" id="searchBox" type="text" className="search-box" placeholder={t("search-input-placeholder-small", { ns: "propertySelectionList" }) as string} onInput={onSearch} noTimer={true} ariaLabel={t("search-input-placeholder-small") as string} />
                                </div>
                            )}
                            <div className={styles.EditRow} key={0}>
                                <SmallHeading allCaps for={""}>{t("4-enotif-heading")}</SmallHeading>
                                <SmallHeading allCaps for={""}>{t("property-list-table.header.column-1", { ns: "propertySelectionList" })}</SmallHeading>
                                <SmallHeading allCaps for={""}>{t("property-list-table.header.column-2", { ns: "propertySelectionList" })}</SmallHeading>
                            </div>
                            {buildList()}

                        </div>
                        <div className={`${styles.SubmitRow} ${styles.Split}`}>
                            <AmpPagination
                                recordsPerPage={pagesize}
                                totalPages={Math.ceil(filteredList?.length as number / pagesize)}
                                setCurrentPage={setCurrentPage}
                                currentPage={currentPage}
                                totalRecords={filteredList?.length as number}
                            />
                            {pendingChanges.length > 0 && <AmpButton onClick={() => { toggleNextStep(2, false); }} type={"submit"} text={t("update")} />}
                        </div>
                    </>)}
                    {currentStep === 2 && (<>
                        <div className={styles.EditTop}>
                            <div className={styles.Scrollable}>
                                <div className={styles.EditRow} key={0}>
                                    <SmallHeading allCaps for={""}>{t("4-enotif-heading")}</SmallHeading>
                                    <SmallHeading allCaps for={""}>{t("property-list-table.header.column-1", { ns: "propertySelectionList" })}</SmallHeading>
                                    <SmallHeading allCaps for={""}>{t("property-list-table.header.column-2", { ns: "propertySelectionList" })}</SmallHeading>
                                </div>
                                {buildPendingList(false)}
                                <AmpPagination
                                    totalPages={Math.ceil(disabledPending?.length as number / 5)}
                                    setCurrentPage={setPendingCurrentPage}
                                    recordsPerPage={5}
                                    currentPage={pendingCurrPage}
                                    totalRecords={disabledPending?.length as number}
                                />
                            </div>
                            <AmpLoadingSpinner loading={enotifState.loading}>
                                <div>
                                    <form>
                                        <AttestText handleConsent={setDisabledConsented} isEnabled={false} />
                                        {/* <AmpCheckBox
                                        blueStyle={true}
                                        handleCheck={(e) => { setDisabledConsented((e.target as HTMLInputElement).checked) }}
                                        id={""}
                                        label={"I Consent"}
                                        hasToolTip={false}
                                        toolTip={""}
                                        defaultChecked={disabledConsented} />
                                    <br /> */}
                                        {/* <AmpButton atlStyle={true} type="cancel" onClick={() => { toggleNextStep(1, true); }} text={t("back")} />
                                    <AmpButton atlStyle={true} type="cancel" onClick={() => { toggleNextStep(3, false); }} text={t("next")} disabled={!disabledConsented} /> */}
                                    </form>
                                </div>
                            </AmpLoadingSpinner>
                        </div>
                        <div className={styles.SubmitRow}>
                            <AmpButton atlStyle={true} type="cancel" onClick={() => { cancel(); }} text={t("cancel")} />
                            <AmpButton atlStyle={true} type="cancel" onClick={() => { toggleNextStep(1, true); }} text={t("back")} />
                            <AmpButton atlStyle={true} type="submit" onClick={() => { toggleNextStep(3, false); }} text={t("next")} disabled={!disabledConsented} />
                        </div>
                    </>)}
                    {currentStep === 3 && (<>
                        <div className={styles.EditTop}>
                            <div className={styles.Scrollable}>
                                <div className={styles.EditRow} key={0}>
                                    <SmallHeading allCaps for={""}>{t("4-enotif-heading")}</SmallHeading>
                                    <SmallHeading allCaps for={""}>{t("property-list-table.header.column-1", { ns: "propertySelectionList" })}</SmallHeading>
                                    <SmallHeading allCaps for={""}>{t("property-list-table.header.column-2", { ns: "propertySelectionList" })}</SmallHeading>
                                </div>
                                {buildPendingList(true)}
                                <AmpPagination
                                    totalPages={Math.ceil(enabledPending?.length as number / 5)}
                                    setCurrentPage={setPendingCurrentPage}
                                    recordsPerPage={5}
                                    currentPage={pendingCurrPage}
                                    totalRecords={enabledPending?.length as number}
                                />
                            </div>
                            <div>
                                <form>
                                    <AttestText handleConsent={setEnabledConsented} isEnabled={true} />
                                </form>
                            </div>
                        </div>
                        <div className={styles.SubmitRow}>
                            <AmpButton atlStyle={true} type="cancel" onClick={() => { cancel(); }} text={t("cancel")} />
                            <AmpButton atlStyle={true} type="cancel" onClick={() => { toggleNextStep(2, true); }} text={t("back")} />
                            <AmpButton atlStyle={true} type="submit" onClick={() => { toggleNextStep(4, false); }} text={t("next")} disabled={!enabledConsented} />
                        </div>
                    </>)}
                    {currentStep === 4 && (<>
                        <div className={styles.EditTop}>
                            <AmpLoadingSpinner loading={enotifState.loading}>
                                <EnoticiationForm
                                    updateKey={checkFormKey}
                                    disabled={false}
                                    formCallback={formCallback}
                                    backButtonEnabled={true}
                                    attestValid={attestValid}
                                    setAttestValid={setAttestValid}
                                    backButtonCallBack={() => toggleNextStep(3, true)}
                                    email={email as string}
                                />
                            </AmpLoadingSpinner>
                             <AmpFormError errorMessage={"Error occured while udating enotification settings"} error={hasError}/>
                        </div>
                       
                        <div className={styles.SubmitRow}>
                            <AmpButton atlStyle={true} type="cancel" onClick={() => { cancel(); }} text={t("cancel")} />
                            <AmpButton atlStyle={true} type="cancel" onClick={() => { toggleNextStep(3, true) }} text={t("back")} />
                            <AmpButton atlStyle={true} type="submit" onClick={() => { submit(); }} text={t("submit")} />
                        </div>
                    </>
                    )}
                </div>
                {/* <div className={styles.SubmitRow}>
                    {currentStep === 1 && <AmpButton onClick={() => { toggleNextStep(2, false); }} type={"submit"} text={"Update"} />}
                    {currentStep !== 1 && <AmpButton atlStyle={true} type="submit" onClick={() => { cancel(); }} text={t("cancel")} />}
                    {currentStep === 4 && <AmpButton atlStyle={true} type="submit" onClick={() => { submit(); }} text={t("submit")} disabled={!attestValid} />}
                </div> */}
            </>)}
    </>
    );
}